import React, { useState } from "react";
import { AddUser } from "../utils/usersAPI";
import {
  Box,
  Button,
  TextField,
  Grid,
  Typography,
  Container,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Swal from "sweetalert2";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/*
  עיצוב משותף לכל השדות:

  - מסגרת אפורה במצב רגיל.
  - מסגרת כהה במעבר עכבר.
  - מסגרת ורודה בזמן פוקוס.
  - מסגרת אדומה רק כאשר קיימת שגיאה.
*/
const accessibleTextFieldStyle = {
  direction: "rtl",

  "& .MuiInputLabel-root": {
    right: 30,
    left: "auto",
    transformOrigin: "top right",
  },

  "& legend": {
    textAlign: "right",
  },

  "& input": {
    textAlign: "right",
  },

  "& textarea": {
    textAlign: "right",
  },

  "& .MuiOutlinedInput-root": {
    backgroundColor: "#fff",

    "& fieldset": {
      borderColor: "#9e9e9e",
    },

    "&:hover fieldset": {
      borderColor: "#555",
    },

    "&.Mui-focused fieldset": {
      borderColor: "#D81B60",
      borderWidth: "2px",
    },

    "&.Mui-error fieldset": {
      borderColor: "#d32f2f",
      borderWidth: "2px",
    },
  },

  /*
    מבטל outline חיצוני שעלול להגיע מקובץ CSS גלובלי.
    מסגרת הפוקוס הוורודה נשארת ומספקת סימון נגיש.
  */
  "& input:focus, & textarea:focus": {
    outline: "none",
  },
};

const initialFormData = {
  name: "",
  email: "",
  password: "",
  phone: "",
  city: "",
  street: "",
  houseNumber: "",
  apartment: "",
  floor: "",
  entrance: "",
  notes: "",
};

const initialErrors = {
  name: "",
  email: "",
  password: "",
  phone: "",
  city: "",
  street: "",
  houseNumber: "",
};

const RegistrationForm = () => {
  const [formData, setFormData] =
    useState(initialFormData);

  const [errors, setErrors] =
    useState(initialErrors);

  const [generalError, setGeneralError] =
    useState("");

  const [showPassword, setShowPassword] =
    useState(false);

  const [isSubmitting, setIsSubmitting] =
    useState(false);

  const navigate = useNavigate();

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setFormData((previousData) => ({
      ...previousData,
      [name]: value,
    }));

    /*
      מסירים את השגיאה של השדה
      ברגע שהמשתמשת מתחילה לתקן אותו.
    */
    if (errors[name]) {
      setErrors((previousErrors) => ({
        ...previousErrors,
        [name]: "",
      }));
    }

    setGeneralError("");
  };

  const validateForm = () => {
    const newErrors = {
      name: "",
      email: "",
      password: "",
      phone: "",
      city: "",
      street: "",
      houseNumber: "",
    };

    const normalizedEmail =
      formData.email.trim().toLowerCase();

    if (!formData.name.trim()) {
      newErrors.name =
        "חובה להזין שם משתמש";
    }

    if (!normalizedEmail) {
      newErrors.email =
        "חובה להזין כתובת מייל";
    } else if (
      !EMAIL_REGEX.test(normalizedEmail)
    ) {
      newErrors.email =
        "כתובת האימייל אינה חוקית";
    }

    if (!formData.password) {
      newErrors.password =
        "חובה להזין סיסמה";
    } else if (
      formData.password.length < 6
    ) {
      newErrors.password =
        "הסיסמה חייבת להכיל לפחות 6 תווים";
    }

    if (!formData.phone.trim()) {
      newErrors.phone =
        "חובה להזין מספר טלפון";
    }

    if (!formData.city.trim()) {
      newErrors.city =
        "חובה להזין עיר";
    }

    if (!formData.street.trim()) {
      newErrors.street =
        "חובה להזין רחוב";
    }

    if (!formData.houseNumber.trim()) {
      newErrors.houseNumber =
        "חובה להזין מספר בניין";
    }

    setErrors(newErrors);

    return !Object.values(newErrors).some(
      Boolean
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isSubmitting) {
      return;
    }

    setGeneralError("");

    if (!validateForm()) {
      setGeneralError(
        "יש לתקן את השדות המסומנים בטופס"
      );
      return;
    }

    const normalizedEmail =
      formData.email.trim().toLowerCase();

    const userToSend = {
      name: formData.name.trim(),
      email: normalizedEmail,
      password: formData.password,
      phone: formData.phone.trim(),

      userAddresses: [
        {
          city: formData.city.trim(),
          street: formData.street.trim(),
          houseNumber:
            formData.houseNumber.trim(),

          apartment:
            formData.apartment.trim() ||
            null,

          floor:
            formData.floor.trim() ||
            null,

          entrance:
            formData.entrance.trim() ||
            null,

          notes:
            formData.notes.trim() ||
            null,

          isDefault: true,
        },
      ],
    };

    try {
      setIsSubmitting(true);

      const res = await AddUser(
        userToSend
      );

      if (
        res.status === 200 ||
        res.status === 201
      ) {
        await Swal.fire({
          icon: "success",
          title: "נרשמת בהצלחה!",
          text:
            "יש להתחבר לאתר עם המייל והסיסמה ולסיים את ההזמנה.",
          confirmButtonText:
            "מעבר להתחברות",
          confirmButtonColor: "#D81B60",
        });

        navigate("/signIn");
      } else {
        setGeneralError(
          "ההרשמה נכשלה. נסי שוב."
        );
      }
    } catch (err) {
      console.error(
        "Registration error:",
        err.response?.data || err
      );

      const serverMessage =
        err.response?.data?.message ||
        err.response?.data ||
        "ההרשמה נכשלה, נסי שנית";

      setGeneralError(
        typeof serverMessage === "string"
          ? serverMessage
          : "ההרשמה נכשלה, נסי שנית"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container
      component="main"
      maxWidth="xs"
      dir="rtl"
      sx={{
        mt: {
          xs: 8,
          sm: 17.5,
        },
        mb: 6,
      }}
    >
      <Box
        component="form"
        noValidate
        onSubmit={handleSubmit}
        aria-labelledby="registration-title"
      >
        <Typography
          id="registration-title"
          component="h1"
          variant="h4"
          align="center"
          gutterBottom
          sx={{
            fontWeight: 700,
            mb: 3,
          }}
        >
          רישום משתמש חדש
        </Typography>

        <Grid container spacing={2}>
          {/* שם משתמש */}
          <Grid item xs={12}>
            <TextField
              id="registration-name"
              label="שם משתמש"
              name="name"
              type="text"
              fullWidth
              required
              autoComplete="name"
              value={formData.name}
              onChange={handleInputChange}
              error={Boolean(errors.name)}
              helperText={errors.name}
              inputProps={{
                maxLength: 100,
              }}
              sx={accessibleTextFieldStyle}
            />
          </Grid>

          {/* מספר טלפון */}
          <Grid item xs={12}>
            <TextField
              id="registration-phone"
              label="מספר טלפון"
              name="phone"
              type="tel"
              fullWidth
              required
              autoComplete="tel"
              value={formData.phone}
              onChange={handleInputChange}
              error={Boolean(errors.phone)}
              helperText={errors.phone}
              inputProps={{
                inputMode: "tel",
                maxLength: 20,
              }}
              sx={accessibleTextFieldStyle}
            />
          </Grid>

          {/* כתובת מייל */}
          <Grid item xs={12}>
            <TextField
              id="registration-email"
              label="כתובת מייל"
              name="email"
              type="email"
              fullWidth
              required
              autoComplete="email"
              value={formData.email}
              onChange={handleInputChange}
              error={Boolean(errors.email)}
              helperText={errors.email}
              inputProps={{
                inputMode: "email",
                maxLength: 150,
              }}
              sx={accessibleTextFieldStyle}
            />
          </Grid>

          {/* סיסמה */}
          <Grid item xs={12}>
            <TextField
              id="registration-password"
              label="סיסמה"
              name="password"
              type={
                showPassword
                  ? "text"
                  : "password"
              }
              fullWidth
              required
              autoComplete="new-password"
              value={formData.password}
              onChange={handleInputChange}
              error={Boolean(
                errors.password
              )}
              helperText={
                errors.password ||
                "הסיסמה צריכה להכיל לפחות 6 תווים"
              }
              inputProps={{
                minLength: 6,
                maxLength: 100,
              }}
              sx={{
                ...accessibleTextFieldStyle,

                "& input": {
                  textAlign: "right",
                  paddingLeft: "45px",
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      type="button"
                      edge="end"
                      onClick={() =>
                        setShowPassword(
                          (
                            previousValue
                          ) =>
                            !previousValue
                        )
                      }
                      aria-label={
                        showPassword
                          ? "הסתרת הסיסמה"
                          : "הצגת הסיסמה"
                      }
                      aria-pressed={
                        showPassword
                      }
                      sx={{
                        color:
                          "text.primary",

                        "&:hover": {
                          backgroundColor:
                            "rgba(0, 0, 0, 0.06)",
                          color:
                            "text.primary",
                        },

                        "&:focus-visible": {
                          outline:
                            "3px solid #111",
                          outlineOffset:
                            "2px",
                        },
                      }}
                    >
                      {showPassword ? (
                        <VisibilityOff
                          aria-hidden="true"
                        />
                      ) : (
                        <Visibility
                          aria-hidden="true"
                        />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* כתובת למשלוח */}
          <Grid item xs={12}>
            <Box
              component="fieldset"
              sx={{
                border: 0,
                p: 0,
                m: 0,
                width: "100%",
              }}
            >
              <Typography
                component="legend"
                variant="h6"
                sx={{
                  mb: 2,
                  fontWeight: 700,
                }}
              >
                כתובת למשלוח
              </Typography>

              <Grid
                container
                spacing={2}
              >
                {/* עיר */}
                <Grid item xs={12}>
                  <TextField
                    id="registration-city"
                    label="עיר"
                    name="city"
                    type="text"
                    fullWidth
                    required
                    autoComplete="address-level2"
                    value={formData.city}
                    onChange={
                      handleInputChange
                    }
                    error={Boolean(
                      errors.city
                    )}
                    helperText={
                      errors.city
                    }
                    inputProps={{
                      maxLength: 100,
                    }}
                    sx={
                      accessibleTextFieldStyle
                    }
                  />
                </Grid>

                {/* רחוב */}
                <Grid item xs={12}>
                  <TextField
                    id="registration-street"
                    label="רחוב"
                    name="street"
                    type="text"
                    fullWidth
                    required
                    autoComplete="address-line1"
                    value={formData.street}
                    onChange={
                      handleInputChange
                    }
                    error={Boolean(
                      errors.street
                    )}
                    helperText={
                      errors.street
                    }
                    inputProps={{
                      maxLength: 150,
                    }}
                    sx={
                      accessibleTextFieldStyle
                    }
                  />
                </Grid>

                {/* מספר בניין */}
                <Grid
                  item
                  xs={12}
                  sm={6}
                >
                  <TextField
                    id="registration-house-number"
                    label="מספר בניין"
                    name="houseNumber"
                    type="text"
                    fullWidth
                    required
                    autoComplete="address-line2"
                    value={
                      formData.houseNumber
                    }
                    onChange={
                      handleInputChange
                    }
                    error={Boolean(
                      errors.houseNumber
                    )}
                    helperText={
                      errors.houseNumber
                    }
                    inputProps={{
                      maxLength: 20,
                    }}
                    sx={
                      accessibleTextFieldStyle
                    }
                  />
                </Grid>

                {/* דירה */}
                <Grid
                  item
                  xs={12}
                  sm={6}
                >
                  <TextField
                    id="registration-apartment"
                    label="דירה (אופציונלי)"
                    name="apartment"
                    type="text"
                    fullWidth
                    autoComplete="off"
                    value={
                      formData.apartment
                    }
                    onChange={
                      handleInputChange
                    }
                    inputProps={{
                      maxLength: 20,
                    }}
                    sx={
                      accessibleTextFieldStyle
                    }
                  />
                </Grid>

                {/* קומה */}
                <Grid
                  item
                  xs={12}
                  sm={6}
                >
                  <TextField
                    id="registration-floor"
                    label="קומה (אופציונלי)"
                    name="floor"
                    type="text"
                    fullWidth
                    autoComplete="off"
                    value={formData.floor}
                    onChange={
                      handleInputChange
                    }
                    inputProps={{
                      maxLength: 20,
                    }}
                    sx={
                      accessibleTextFieldStyle
                    }
                  />
                </Grid>

                {/* כניסה */}
                <Grid
                  item
                  xs={12}
                  sm={6}
                >
                  <TextField
                    id="registration-entrance"
                    label="כניסה (אופציונלי)"
                    name="entrance"
                    type="text"
                    fullWidth
                    autoComplete="off"
                    value={
                      formData.entrance
                    }
                    onChange={
                      handleInputChange
                    }
                    inputProps={{
                      maxLength: 20,
                    }}
                    sx={
                      accessibleTextFieldStyle
                    }
                  />
                </Grid>

                {/* הערות לשליח */}
                <Grid item xs={12}>
                  <TextField
                    id="registration-notes"
                    label="הערות לשליח (אופציונלי)"
                    name="notes"
                    fullWidth
                    multiline
                    rows={3}
                    value={formData.notes}
                    onChange={
                      handleInputChange
                    }
                    inputProps={{
                      maxLength: 500,
                    }}
                    helperText={`${formData.notes.length}/500`}
                    sx={
                      accessibleTextFieldStyle
                    }
                  />
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>

        {generalError && (
          <Typography
            id="registration-general-error"
            component="p"
            role="alert"
            aria-live="assertive"
            color="error"
            sx={{
              mt: 2,
              mb: 1,
              textAlign: "right",
            }}
          >
            {generalError}
          </Typography>
        )}

        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={isSubmitting}
          aria-disabled={isSubmitting}
          aria-describedby={
            generalError
              ? "registration-general-error"
              : undefined
          }
          sx={{
            mt: 3,
            minHeight: 44,
            fontWeight: 700,
            backgroundColor: "#D81B60",

            "&:hover": {
              backgroundColor: "#AD1457",
            },

            "&:focus-visible": {
              outline: "3px solid #111",
              outlineOffset: "3px",
            },

            "&.Mui-disabled": {
              backgroundColor:
                "rgba(216, 27, 96, 0.45)",
              color: "#fff",
            },
          }}
        >
          {isSubmitting
            ? "מבצע הרשמה..."
            : "הרשמה"}
        </Button>
      </Box>
    </Container>
  );
};

export default RegistrationForm;